"use client"

import React, { useRef, useSyncExternalStore } from "react"
import {
  LazyMotion,
  domAnimation,
  m,
  useInView,
  type UseInViewOptions,
} from "framer-motion"

export interface BlurRevealProps {
  children: React.ReactNode
  /** External visibility control — when provided the internal useInView is ignored. */
  animateWhen?: boolean
  delay?: number
  duration?: number
  blur?: number
  once?: boolean
  margin?: UseInViewOptions["margin"]
  style?: React.CSSProperties
  className?: string
}

const BlurReveal = ({
  children,
  animateWhen,
  delay = 0,
  duration = 0.6,
  blur = 12,
  once = true,
  margin = "-40px",
  style,
  className,
}: BlurRevealProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const isHydrated = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false
  )
  const internalInView = useInView(ref, { once, margin })
  const isVisible = animateWhen !== undefined ? animateWhen : internalInView

  if (!isHydrated) {
    return (
      <div className={className} ref={ref} style={style}>
        {children}
      </div>
    )
  }

  return (
    <LazyMotion features={domAnimation}>
      <m.div
        ref={ref}
        animate={
          isVisible
            ? { filter: "blur(0px)", opacity: 1 }
            : { filter: `blur(${blur}px)`, opacity: 0 }
        }
        className={className}
        initial={{ filter: `blur(${blur}px)`, opacity: 0 }}
        style={style}
        transition={{ delay, duration, ease: [0.215, 0.61, 0.355, 1] }}
      >
        {children}
      </m.div>
    </LazyMotion>
  )
}

export default BlurReveal
